import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, ButtonGroup } from 'react-bootstrap';
import axios from 'axios'; // Changed from import * as axios
import moment from 'moment';
import api from '../Components/ApiClient';
import { useParams } from 'react-router-dom';

// TODO: Define more specific types for checkpoint and competitor if possible
interface Checkpoint {
	id: string; // Assuming checkpoints have a unique ID
	timestamp: Date | string; // Allow string for initial data, convert to Date
	transponderId: string;
	competitorId?: string;
	deleted?: boolean;
	// Add other checkpoint properties if any
}

interface Competitor {
	id: string;
	name: string;
	number?: string; // Assuming number is optional
	// Add other competitor properties if any
}

const DecoderPage: React.FC = () => {
	const { raceid } = useParams<{ raceid: string }>();

	const [ipAddress, setIpAddressState] = useState('192.168.1.170');
	const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([]);
	const [log, setLog] = useState<string[]>([]);
	const [competitors, setCompetitors] = useState<Record<string, Competitor>>({});
	const [selectedCompetitorToLink, setSelectedCompetitorToLink] = useState<string>('');
	const [websocketStatus, setWebsocketStatus] = useState('disconnected');
	const [showLog, setShowLog] = useState(false); // Added from original state

	const webSocketRef = useRef<WebSocket | null>(null);

	const addLogEntry = useCallback((logMessage: string) => {
		setLog(prevLog => [...prevLog, `${moment().format('HH:mm:ss')} : ${logMessage}`]);
	}, []);

	const handleIpAddressChange = useCallback((newIpAddress: string) => {
		setIpAddressState(newIpAddress);
		window.localStorage.setItem('decoderAddress', newIpAddress);
	}, []);
	
	const loadCheckpoints = useCallback(async () => {
		if (!raceid) return;
		try {
			const response: Checkpoint[] = await api.request(`checkpoints?raceid=${raceid}`);
			setCheckpoints(response.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())); // Assuming reverse was for sorting by time desc
		} catch (error) {
			addLogEntry(`Error loading checkpoints: ${error.message}`);
		}
	}, [raceid, addLogEntry]);

	const loadCompetitors = useCallback(async () => {
		if (!raceid) return;
		try {
			const response: Competitor[] = await api.request(`competitors/inrace?raceid=${raceid}`);
			setCompetitors(response.reduce((map, obj) => {
				map[obj.id] = obj;
				return map;
			}, {} as Record<string, Competitor>));
		} catch (error) {
			addLogEntry(`Error loading competitors: ${error.message}`);
		}
	}, [raceid, addLogEntry]);

	useEffect(() => {
		loadCheckpoints();
		loadCompetitors();
		const storedDecoderAddress = window.localStorage.getItem('decoderAddress');
		if (storedDecoderAddress) {
			setIpAddressState(storedDecoderAddress);
		}
	}, [loadCheckpoints, loadCompetitors]); // raceid is a dependency of loadCheckpoints/loadCompetitors

	useEffect(() => {
		// WebSocket cleanup effect
		return () => {
			if (webSocketRef.current) {
				webSocketRef.current.onclose = null; // Avoid triggering close logic during cleanup
				webSocketRef.current.onerror = null;
				webSocketRef.current.onmessage = null;
				webSocketRef.current.onopen = null;
				webSocketRef.current.close();
				webSocketRef.current = null;
			}
		};
	}, []);

	const uploadCheckpoint = useCallback(async (checkpointData: { timestamp: Date, transponder: string }) => {
		if (!raceid) return;
		try {
			const response: Checkpoint = await api.post(`checkpoints/add/${raceid}`, { timestamp: checkpointData.timestamp, transponderId: checkpointData.transponder });
			addLogEntry(`Uploaded checkpoint: ${new Date(checkpointData.timestamp).toUTCString()} - ${checkpointData.transponder} - ${response.competitorId || 'N/A'}`);
			setCheckpoints(prevCheckpoints => [response, ...prevCheckpoints].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()));
		} catch (e) {
			addLogEntry(`Failed to upload checkpoint: ${new Date(checkpointData.timestamp).toUTCString()} - ${checkpointData.transponder}`);
			if (e instanceof Error) addLogEntry(e.message); else addLogEntry(String(e));
		}
	}, [raceid, addLogEntry]);

	const addSingleCheckpointFromLine = useCallback((line: string) => {
		const data = line.replace("\\n", "").split(';');
		if (data.length >= 2 && data[0] && data[1]) {
			const timestampNum = Number(data[0]);
			if (!isNaN(timestampNum)) {
				const checkpoint = {
					timestamp: new Date(timestampNum), // Assuming timestamp is in milliseconds
					transponder: data[1],
				};
				uploadCheckpoint(checkpoint);
			} else {
				addLogEntry(`Invalid timestamp in checkpoint data: ${data[0]}`);
			}
		} else {
			addLogEntry(`Invalid checkpoint line format: ${line}`);
		}
	}, [uploadCheckpoint, addLogEntry]);

	const addMultipleCheckpointsFromText = useCallback((linesText: string) => {
		for (const line of linesText.split('\\n')) {
			const cleanedLine = line.replace('\\r', '');
			if (cleanedLine !== '') {
				addSingleCheckpointFromLine(cleanedLine);
			}
		}
	}, [addSingleCheckpointFromLine]);
	
	const fetchAllCheckpointsFromDecoder = useCallback(async () => {
		try {
			// Assuming axios.get is the correct usage after import change
			const statusRequest = await axios.get(`http://${ipAddress}/checkpoints`);
			if (statusRequest.status === 200 && typeof statusRequest.data === 'string') {
				addMultipleCheckpointsFromText(statusRequest.data);
			} else {
				addLogEntry(`Failed to get all checkpoints or unexpected data format. Status: ${statusRequest.status}`);
			}
		} catch (error) {
			if (error instanceof Error) addLogEntry(`Error getting all checkpoints: ${error.message}`); else addLogEntry(String(error));
		}
	}, [ipAddress, addMultipleCheckpointsFromText, addLogEntry]);

	const fetchCheckpointsFromTimestamp = useCallback(async (timestamp: number) => {
		try {
			const statusRequest = await axios.get(`http://${ipAddress}/checkpointsfrom?timestamp=${timestamp}`);
			if (statusRequest.status === 200 && typeof statusRequest.data === 'string') {
				addMultipleCheckpointsFromText(statusRequest.data);
			} else {
				addLogEntry(`Failed to get checkpoints from timestamp or unexpected data format. Status: ${statusRequest.status}`);
			}
		} catch (error) {
			if (error instanceof Error) addLogEntry(`Error getting checkpoints from timestamp: ${error.message}`); else addLogEntry(String(error));
		}
	}, [ipAddress, addMultipleCheckpointsFromText, addLogEntry]);

	const connectToDecoder = useCallback(async () => {
		if (webSocketRef.current && webSocketRef.current.readyState === WebSocket.OPEN) {
			addLogEntry('Already connected.');
			return;
		}
		if (webSocketRef.current && webSocketRef.current.readyState === WebSocket.CONNECTING) {
			addLogEntry('Connection attempt in progress.');
			return;
		}

		// Clean up any existing connection before creating a new one
		if (webSocketRef.current) {
			webSocketRef.current.close();
			webSocketRef.current = null;
		}
		
		addLogEntry('Attempting to connect to decoder via WebSocket...');
		setWebsocketStatus('connecting');

		// Fetch initial checkpoints before establishing WebSocket
		// This logic was present in the original class component's connect method
		try {
			if (checkpoints.length > 0 && checkpoints[0]?.timestamp) {
				const timestampValue = moment(checkpoints[0].timestamp).valueOf();
				addLogEntry(`Getting checkpoints from timestamp: ${new Date(timestampValue).toISOString()}`);
				await fetchCheckpointsFromTimestamp(timestampValue);
			} else {
				addLogEntry('No existing checkpoints in UI, fetching all from decoder.');
				await fetchAllCheckpointsFromDecoder();
			}
		} catch (error) {
			addLogEntry('Error fetching initial checkpoints before WebSocket connect.');
		}


		const ws = new WebSocket(`ws://${ipAddress}/ws`);
		webSocketRef.current = ws;

		ws.onopen = () => {
			addLogEntry('WebSocket connection established.');
			setWebsocketStatus('connected');
		};

		ws.onmessage = (event) => {
			if (typeof event.data === 'string') {
				addSingleCheckpointFromLine(event.data);
			}
		};

		ws.onclose = (event) => {
			addLogEntry(`WebSocket connection closed. Code: ${event.code}, Reason: ${event.reason || 'N/A'}`);
			setWebsocketStatus('disconnected');
			if (webSocketRef.current === ws) { // Ensure we are not interfering with a new connection
				webSocketRef.current = null;
			}
		};

		ws.onerror = (event) => {
			addLogEntry('WebSocket connection error.');
			console.error("WebSocket Error:", event);
			setWebsocketStatus('error');
			if (webSocketRef.current === ws) {
				webSocketRef.current = null;
			}
		};
	}, [ipAddress, addLogEntry, addSingleCheckpointFromLine, checkpoints, fetchAllCheckpointsFromDecoder, fetchCheckpointsFromTimestamp]);

	const resetRaceCheckpointsInDb = useCallback(async () => {
		if (!raceid) return;
		if (!window.confirm('Are you sure you want to reset all checkpoints for this race in the database?')) return;
		try {
			await api.post(`checkpoints/reset/${raceid}`, null);
			addLogEntry('Cleared all race checkpoints in database.');
			setCheckpoints([]); // Clear UI
		} catch (error) {
			if (error instanceof Error) addLogEntry(`Error resetting race checkpoints: ${error.message}`); else addLogEntry(String(error));
		}
	}, [raceid, addLogEntry]);

	const insertManualCheckpointsPrompt = useCallback(() => {
		const checkpointData = window.prompt('Enter checkpoint data (semicolon-separated, one per line):');
		if (checkpointData) {
			addMultipleCheckpointsFromText(checkpointData);
		}
	}, [addMultipleCheckpointsFromText]);

	const archiveDecoderCheckpoints = useCallback(async () => {
		if (window.confirm('Are you sure you want to archive checkpoints on the decoder?')) {
			try {
				const statusRequest = await axios.get(`http://${ipAddress}/archivecheckpoints`);
				if (statusRequest.status === 200) {
					addLogEntry('Archived checkpoints on decoder.');
				} else {
					addLogEntry(`Failed to archive. Status: ${statusRequest.status}`);
				}
			} catch (error) {
				if (error instanceof Error) addLogEntry(`Error archiving checkpoints: ${error.message}`); else addLogEntry(String(error));
			}
		}
	}, [ipAddress, addLogEntry]);

	const trashDecoderCheckpoints = useCallback(async () => {
		if (window.confirm('Are you sure you want to trash checkpoints on the decoder?')) {
			try {
				const statusRequest = await axios.get(`http://${ipAddress}/trashcheckpoints`);
				if (statusRequest.status === 200) {
					addLogEntry('Trashed checkpoints on decoder.');
				} else {
					addLogEntry(`Failed to trash. Status: ${statusRequest.status}`);
				}
			} catch (error) {
				if (error instanceof Error) addLogEntry(`Error trashing checkpoints: ${error.message}`); else addLogEntry(String(error));
			}
		}
	}, [ipAddress, addLogEntry]);

	const setDecoderTime = useCallback(async () => {
		const epoch = Math.round((new Date()).valueOf() / 1000);
		try {
			const statusRequest = await axios.get(`http://${ipAddress}/updatesettings?time=${epoch}`);
			if (statusRequest.status === 200) {
				addLogEntry('Time set on decoder.');
			} else {
				addLogEntry(`Failed to set time. Status: ${statusRequest.status}`);
			}
		} catch (error) {
			if (error instanceof Error) addLogEntry(`Error setting time: ${error.message}`); else addLogEntry(String(error));
		}
	}, [ipAddress, addLogEntry]);
	
	const updateLocalCheckpointState = useCallback((checkpointId: string, values: Partial<Checkpoint>) => {
		setCheckpoints(prevCheckpoints =>
			prevCheckpoints.map(cp => (cp.id === checkpointId ? { ...cp, ...values } : cp))
		);
	}, []);

	const deleteCheckpointFromDb = useCallback(async (checkpoint: Checkpoint) => {
		if (!checkpoint.id) return;
		if (!window.confirm(`Are you sure you want to delete checkpoint for transponder ${checkpoint.transponderId} at ${moment(checkpoint.timestamp).format('HH:mm:ss')}?`)) return;
		try {
			await api.delete(`checkpoints/delete/${checkpoint.id}`);
			updateLocalCheckpointState(checkpoint.id, { deleted: true });
			addLogEntry(`Checkpoint ${checkpoint.id} marked as deleted.`);
		} catch (error) {
			if (error instanceof Error) addLogEntry(`Error deleting checkpoint ${checkpoint.id}: ${error.message}`); else addLogEntry(String(error));
		}
	}, [updateLocalCheckpointState, addLogEntry]);

	const undeleteCheckpointInDb = useCallback(async (checkpoint: Checkpoint) => {
		if (!checkpoint.id) return;
		try {
			await api.put(`checkpoints/undelete/${checkpoint.id}`, null);
			updateLocalCheckpointState(checkpoint.id, { deleted: false });
			addLogEntry(`Checkpoint ${checkpoint.id} restored.`);
		} catch (error) {
			if (error instanceof Error) addLogEntry(`Error undeleting checkpoint ${checkpoint.id}: ${error.message}`); else addLogEntry(String(error));
		}
	}, [updateLocalCheckpointState, addLogEntry]);

	const linkTransponderToCompetitor = useCallback(async (transponderId: string, competitorIdToLink: string) => {
		if (!raceid || !competitorIdToLink) {
			addLogEntry("Race ID or Competitor ID missing for linking.");
			return;
		}
		try {
			await api.post(`checkpoints/linkcheckpoints?raceid=${raceid}&competitorid=${competitorIdToLink}&transponderId=${transponderId}`, null);
			addLogEntry(`Linked transponder ${transponderId} to competitor ${competitors[competitorIdToLink]?.name || competitorIdToLink}. Reloading checkpoints...`);
			loadCheckpoints(); // Reload checkpoints to reflect linkage
			setSelectedCompetitorToLink(''); // Reset dropdown
		} catch (error) {
			if (error instanceof Error) addLogEntry(`Error linking transponder: ${error.message}`); else addLogEntry(String(error));
		}
	}, [raceid, loadCheckpoints, addLogEntry, competitors]);

	const displayedLog = useCallback(() => {
		const logLength = log.length;
		const displayLength = 5; // Number of log entries to show when not fully expanded
		if (logLength > displayLength && !showLog) {
			return log.slice(logLength - displayLength);
		}
		return log;
	}, [log, showLog]);

	return (
		<div>
			<div>
				<h2>Connect to decoder</h2>
			</div>

			<div>
				<div className="mb-3">
					<label htmlFor="decoderIpAddress" className="form-label">Decoder IP Address</label>
					<input 
						type="text" 
						id="decoderIpAddress" 
						className="form-control" 
						value={ipAddress} 
						onChange={(event) => handleIpAddressChange(event.target.value)} 
					/>
				</div>

				<ButtonGroup className="mb-3">
					<Button variant="primary" onClick={connectToDecoder} disabled={websocketStatus === 'connected' || websocketStatus === 'connecting'}>
						{websocketStatus === 'connected' ? 'Connected' : websocketStatus === 'connecting' ? 'Connecting...' : 'Connect'}
					</Button>
					<Button variant="primary" onClick={insertManualCheckpointsPrompt}>Insert Checkpoints</Button>
					<Button variant="warning" onClick={fetchAllCheckpointsFromDecoder}>Get All Decoder Checkpoints</Button>
					<Button variant="danger" onClick={resetRaceCheckpointsInDb}>Reset Race Checkpoints (DB)</Button>
				</ButtonGroup>
				<ButtonGroup className="mb-3">
					<Button variant="warning" onClick={archiveDecoderCheckpoints}>Archive Decoder CPs</Button>
					<Button variant="warning" onClick={trashDecoderCheckpoints}>Trash Decoder CPs</Button>
					<Button variant="info" onClick={setDecoderTime}>Set Decoder Time</Button>
				</ButtonGroup>

				<div>
					<Button variant="link" onClick={() => setShowLog(!showLog)}>
						{showLog ? 'Hide Log' : 'Show Full Log'} (Status: {websocketStatus})
					</Button>
					<div>
						{displayedLog().map((logEntry, i) => (
							<div key={`${logEntry}-${i}`}>{logEntry}</div> // Added index to key for potential duplicate log messages
						))}
					</div>
				</div>

				<table className="table table-striped mt-3">
					<thead>
						<tr>
							<th>Timestamp</th>
							<th>Transponder</th>
							<th>Competitor</th>
							<th>Actions</th>
						</tr>
					</thead>
					<tbody>
						{checkpoints.map((checkpoint) => (
							<tr key={checkpoint.id || checkpoint.timestamp.toString()} className={checkpoint.deleted ? 'bg-danger' : ''}>
								<td>
									{moment(checkpoint.timestamp).format('HH:mm:ss.SSS')}
								</td>
								<td>{checkpoint.transponderId}</td>
								<td>
									{checkpoint.competitorId && competitors[checkpoint.competitorId] ? 
										`${competitors[checkpoint.competitorId].name} (#${competitors[checkpoint.competitorId].number || 'N/A'})` :
										(
											<div>
												<select 
													value={selectedCompetitorToLink} 
													onChange={(e) => setSelectedCompetitorToLink(e.target.value)}
													className="form-control form-control-sm d-inline-block w-auto mr-2"
												>
													<option value="">--- Choose competitor ---</option>
													{Object.values(competitors).map((competitor) => (
														<option key={competitor.id} value={competitor.id}>
															{competitor.name} (#{competitor.number || 'N/A'})
														</option>
													))}
												</select>
												{selectedCompetitorToLink && (
													<Button 
														variant="primary" 
														size="sm"
														onClick={() => linkTransponderToCompetitor(checkpoint.transponderId, selectedCompetitorToLink)}
													>
														Link
													</Button>
												)}
											</div>
										)
									}
								</td>
								<td>
									{checkpoint.deleted ? (
										<Button variant="success" size="sm" onClick={() => undeleteCheckpointInDb(checkpoint)}>Undelete</Button>
									) : (
										<Button variant="danger" size="sm" onClick={() => deleteCheckpointFromDb(checkpoint)}>Delete</Button>
									)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
};

export default DecoderPage;